import { CASE_BASE_UNIT_LABEL, RESULT_UNIT_KIND } from "./constants";

// Pairs result rows that share a non-null `group` (e.g. "cerveja" splits into
// tradicional/artesanal) so they render as sibling blocks instead of two
// disconnected cards. Rows with `group: null` render standalone. No
// category-key special-casing — driven entirely by the backend response shape.
export function groupResults(results) {
  const seen = new Set();
  const groups = [];
  results.forEach((r) => {
    if (seen.has(r.key)) return;
    if (r.group) {
      const siblings = results.filter((x) => x.group === r.group);
      siblings.forEach((s) => seen.add(s.key));
      groups.push({ type: "group", groupKey: r.group, items: siblings });
    } else {
      seen.add(r.key);
      groups.push({ type: "single", items: [r] });
    }
  });
  return groups;
}

// "unavailable": no usable stock for the category right now.
// "pending": category has a target quantity but no products to suggest yet.
//   With the v5 backend this is effectively unreachable in practice (any
//   category reaching the frontend already passed the same in-stock check
//   /options used to list it), but kept as a safe fallback.
// "ready": full item list + qty steppers + add-to-cart.
export function getBlockDisplayState(result) {
  if (!result.catalog_available) return "unavailable";
  if ((result.available_products || []).length === 0) return "pending";
  return "ready";
}

// --- sale_mode-aware item accessors --------------------------------------
// ProductFinder returns 3 item shapes keyed by `sale_mode` (barrel/case/
// bottle — see backend handoff v5). These helpers hide that distinction so
// the rest of the UI can treat any item uniformly.

// The quantity the [-][+] stepper actually controls: case_quantity (CAIXAS)
// for sale_mode "case", quantity (barris/garrafas) for everything else.
export function itemPrimaryQuantity(item) {
  return item.sale_mode === "case" ? item.case_quantity : item.quantity;
}

// How much of the category's base unit (ml for barril, liters for cerveja's
// case_liters, raw units — garrafas/latas — for everything else) one unit of
// the item's steppable quantity covers.
export function itemCoverage(item) {
  if (item.sale_mode === "barrel") return item.unit_volume_ml;
  if (item.sale_mode === "case") return item.case_liters != null ? item.case_liters : item.units_per_case;
  return 1;
}

export function resultUnitKind(result) {
  return RESULT_UNIT_KIND[result.key] || "units";
}

// Human string for a raw amount already expressed in the category's base
// unit (ml/liters/units) — e.g. 126000 -> "126 L", 64.8 -> "64,8 L",
// 120 -> "120 latas".
export function formatBaseAmount(result, rawAmount) {
  const kind = resultUnitKind(result);
  if (kind === "ml") {
    return `${(rawAmount / 1000).toLocaleString("pt-br", { maximumFractionDigits: 1 })} L`;
  }
  if (kind === "liters") {
    return `${Number(rawAmount).toLocaleString("pt-br", { maximumFractionDigits: 1 })} L`;
  }
  const n = Math.round(rawAmount);
  const words = CASE_BASE_UNIT_LABEL[result.key];
  const word = words ? (n === 1 ? words.singular : words.plural) : result.commercial_unit_label;
  return `${n} ${word}`;
}

// Live progress for a block, recomputed from the user's current quantities —
// summed across EVERY available product (not just the ones the backend
// originally suggested), since "Ver todas as opções"/"Trocar produto" let the
// user put quantity on any mapped product. `currentCases` only applies to
// sale_mode "case" (drinks_prontos/vinho/espumante/cerveja); it's the number
// of caixas actually on the order right now, shown alongside the base-unit
// amount per correction C6 ("10 caixas (120 latas)").
export function computeBlockProgress(result, itemQuantities) {
  const keyFor = (batchId) => `${result.key}:${batchId}`;
  let currentBase = 0;
  let currentCases = 0;
  (result.available_products || []).forEach((item) => {
    const qty = itemQuantities[keyFor(item.batch_id)] ?? 0;
    if (item.sale_mode === "case") currentCases += qty;
    currentBase += qty * itemCoverage(item);
  });
  const target = result.needed_quantity;
  const isComplete = currentBase + 1e-6 >= target;
  return { currentBase, currentCases, target, isComplete };
}

export function formatShortfall(result, currentBase) {
  const remaining = Math.max(result.needed_quantity - currentBase, 0);
  return formatBaseAmount(result, remaining);
}

// "Distribuir automaticamente". The backend owns the rodízio-by-priority
// distribution entirely — every result now carries `redistributed_items`
// (handoff v7/C22: previously only barril categories had it, with
// suggested_items as the fallback for everything else; now it's universal,
// always spread across different products/brands in rodízio). The default
// pre-fill (`suggested_items`) is a separate, category-dependent concentration
// policy (handoff v7/C22: only Drinks Prontos and Cerveja artesanal spread by
// default, everything else concentrates on the top-priority product) — not
// relevant here. The frontend must NOT recompute any of this; the button just
// resets the block's quantities to what the backend provided.
export function distributionTarget(result) {
  return result.redistributed_items || [];
}

export function resetToRedistributed(result) {
  const out = {};
  distributionTarget(result).forEach((item) => {
    out[item.batch_id] = itemPrimaryQuantity(item);
  });
  return out;
}

// Flattens every "ready" block's available products (not just the ones that
// started with quantity > 0 — the user may have added previously-zero items
// via "Ver todas as opções" or "Trocar produto") into a single list carrying
// the user's current live quantity, keeping only items actually wanted
// (quantity > 0). Shared by "Adicionar tudo ao carrinho" and the order
// summary so both always agree on what's being added and what it costs.
export function collectSelectedItems(results, itemQuantities) {
  return results
    .filter((r) => r.catalog_available)
    .flatMap((r) =>
      (r.available_products || []).map((item) => ({
        key: `${r.key}:${item.batch_id}`,
        resultKey: r.key,
        product_id: item.product_id,
        product_name: item.product_name,
        batch_id: item.batch_id,
        unit_price: item.unit_price,
        // Price per steppable unit: a caixa for sale_mode "case", a
        // barril/garrafa otherwise — what `quantity` below actually counts.
        coverageUnitPrice: item.sale_mode === "case" ? item.case_price : item.unit_price,
      }))
    )
    .map((si) => {
      const quantity = itemQuantities[si.key] ?? 0;
      return { ...si, quantity, amount: quantity * si.coverageUnitPrice };
    })
    .filter((si) => si.quantity > 0);
}

// Per-block completion (only counts "ready" blocks — pending/unavailable
// blocks have no items to complete) plus the category labels behind any
// pending/unavailable block, so the summary can tell the user what's
// missing rather than silently omitting it from the total.
export function summarizeBlockStates(results, itemQuantities) {
  let readyTotal = 0;
  let readyComplete = 0;
  const pendingLabels = [];
  const unavailableLabels = [];

  results.forEach((r) => {
    const state = getBlockDisplayState(r);
    if (state === "pending") {
      pendingLabels.push(r.label);
      return;
    }
    if (state === "unavailable") {
      unavailableLabels.push(r.label);
      return;
    }
    readyTotal += 1;
    const { isComplete } = computeBlockProgress(r, itemQuantities);
    if (isComplete) readyComplete += 1;
  });

  return { readyTotal, readyComplete, pendingLabels, unavailableLabels };
}
